import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, BookOpen, MessageSquare, Users, Compass, BookMarked, Palette } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/hooks/useLanguage";

// Sample story data with translation keys
const getStoryData = (t: (key: string) => string) => [
  {
    id: 1,
    title: t('landing.exampleStories.dragonsRiddle.title'),
    excerpt: t('landing.exampleStories.dragonsRiddle.excerpt'),
    genre: ["fantasy", "adventure"].map(g => t(`storySetup.genres.${g}`)),
    themes: ["quest", "courageBravery", "ancientWisdom"].map(theme => t(`storySetup.themeOptions.${theme}`)),
    environment: ["mountainCavern", "dragonLair"].map(env => t(`storySetup.environments.${env}`)),
    imageUrl: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1184&q=80",
    ai_suggestions: [
      t('landing.exampleStories.dragonsRiddle.suggestion1'),
      t('landing.exampleStories.dragonsRiddle.suggestion2'),
      t('landing.exampleStories.dragonsRiddle.suggestion3')
    ]
  },
  {
    id: 2,
    title: t('landing.exampleStories.echoesFromTomorrow.title'),
    excerpt: t('landing.exampleStories.echoesFromTomorrow.excerpt'),
    genre: ["scienceFiction", "mystery"].map(g => t(`storySetup.genres.${g}`)),
    themes: ["timeTravel", "technology", "paradox"].map(theme => t(`storySetup.themeOptions.${theme}`)),
    environment: ["researchLaboratory", "futureCommunications"].map(env => t(`storySetup.environments.${env}`)),
    imageUrl: "https://images.unsplash.com/photo-1470770841072-f978cf4d019e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
    ai_suggestions: [
      t('landing.exampleStories.echoesFromTomorrow.suggestion1'),
      t('landing.exampleStories.echoesFromTomorrow.suggestion2'),
      t('landing.exampleStories.echoesFromTomorrow.suggestion3')
    ]
  },
  {
    id: 3,
    title: t('landing.exampleStories.forgottenLibrary.title'),
    excerpt: t('landing.exampleStories.forgottenLibrary.excerpt'),
    genre: ["mystery", "fantasy"].map(g => t(`storySetup.genres.${g}`)),
    themes: ["knowledge", "magic", "discovery"].map(theme => t(`storySetup.themeOptions.${theme}`)),
    environment: ["magicalLibrary", "rainyNight"].map(env => t(`storySetup.environments.${env}`)),
    imageUrl: "https://images.unsplash.com/photo-1476231682828-37e571bc172f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80",
    ai_suggestions: [
      t('landing.exampleStories.forgottenLibrary.suggestion1'),
      t('landing.exampleStories.forgottenLibrary.suggestion2'),
      t('landing.exampleStories.forgottenLibrary.suggestion3')
    ]
  }
];

export const StoryPreview = () => {
  const { t } = useLanguage();
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [autoplay, setAutoplay] = useState(true);

  // Generate translated stories based on current language
  const EXAMPLE_STORIES = getStoryData(t);
  const currentStory = EXAMPLE_STORIES[currentStoryIndex];

  useEffect(() => {
    // Auto-advance the carousel every 8 seconds if autoplay is enabled
    if (!autoplay) return;
    
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentStoryIndex((prev) => 
        prev === EXAMPLE_STORIES.length - 1 ? 0 : prev + 1
      );
    }, 8000);
    
    return () => clearInterval(interval);
  }, [autoplay]);

  const handlePrevious = () => {
    setAutoplay(false); // Disable autoplay when manually navigating
    setDirection(-1);
    setCurrentStoryIndex((prev) => 
      prev === 0 ? EXAMPLE_STORIES.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setAutoplay(false); // Disable autoplay when manually navigating
    setDirection(1);
    setCurrentStoryIndex((prev) => 
      prev === EXAMPLE_STORIES.length - 1 ? 0 : prev + 1
    );
  };

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 800 : -800,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 800 : -800,
      opacity: 0,
    })
  };

  return (
    <section className="py-20 relative overflow-hidden" id="story-preview">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A1F2C]/80 to-[#7E69AB]/80" />
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1518709268805-4e9042af9f23?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1184&q=80')] bg-cover bg-center opacity-10" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">{t('landing.storyPreview.header')}</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {t('landing.storyPreview.description').replace('{{appName}}', 'TaleCraft')}
          </p>
        </div>

        <div className="flex items-center justify-center relative">
          {/* Story carousel - increased width by 30% */}
          <div className="relative w-full max-w-[85rem] h-[650px] overflow-hidden rounded-lg backdrop-blur-md bg-black/20">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentStoryIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                className="absolute inset-0 flex"
              >
                <div className="flex flex-col md:flex-row w-full">
                  {/* Image section with enhanced hover overlay */}
                  <div 
                    className="relative w-full md:w-2/5 h-80 md:h-full bg-cover bg-center rounded-t-lg md:rounded-l-lg md:rounded-tr-none group"
                    style={{ backgroundImage: `url(${currentStory.imageUrl})` }}
                  >
                    {/* Base gradient */}
                    <div className="w-full h-full bg-gradient-to-r from-black/60 via-black/40 to-transparent md:rounded-l-lg flex flex-col justify-between">
                      {/* Top section with genres */}
                      <div className="p-6">
                      {/* <div className="p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300"> */}
                        <div className="flex items-center gap-2 mb-3 text-white/80">
                          <BookMarked className="w-4 h-4" />
                          <span className="text-xs uppercase tracking-wider">{t('landing.storyPreview.genres')}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {currentStory.genre.map((genre, index) => (
                            <span key={index} className="px-2 py-1 bg-purple-700/70 text-white text-xs rounded-full">
                              {genre}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Middle section with themes that appears on hover */}
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3 text-white/80">
                          <Palette className="w-4 h-4" />
                          <span className="text-xs uppercase tracking-wider">{t('landing.storyPreview.themes')}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {currentStory.themes.map((theme, index) => (
                            <span key={index} className="px-2 py-1 bg-indigo-600/70 text-white text-xs rounded-full">
                              {theme}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Bottom section with environment that appears on hover */}
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-3 text-white/80">
                          <Compass className="w-4 h-4" />
                          <span className="text-xs uppercase tracking-wider">{t('landing.storyPreview.environment')}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {currentStory.environment.map((env, index) => (
                            <span key={index} className="px-2 py-1 bg-emerald-700/70 text-white text-xs rounded-full">
                              {env}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content section */}
                  <div className="w-full md:w-3/5 p-6 md:p-10 flex flex-col justify-between overflow-y-auto">
                    <div>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{currentStory.title}</h3>
                      <p className="text-gray-300 text-lg leading-relaxed mb-6">
                        "{currentStory.excerpt}"
                      </p>
                      
                      {/* AI Suggestions - similar to StoryPage */}
                      <div className="space-y-4 mt-4">
                        <div className="grid gap-3 md:grid-cols-1">
                          {currentStory.ai_suggestions.map((option, index) => (
                            <Button
                              key={index}
                              variant="outline"
                              className="h-auto whitespace-normal py-3 text-left cursor-default opacity-80 hover:opacity-100"
                              disabled
                            >
                              {option}
                            </Button>
                          ))}
                        </div>
                        
                        <div className="mt-4 pt-2 border-t border-gray-700">
                          <div className="mt-2">
                            <Input
                              placeholder={t('landing.storyPreview.writeResponse')}
                              className="bg-black/20 border-gray-700 text-gray-300"
                              maxLength={2000}
                              disabled
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            
            {/* Navigation buttons */}
            <button 
              onClick={handlePrevious} 
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
              aria-label={t('landing.storyPreview.previousStory')}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button 
              onClick={handleNext} 
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
              aria-label={t('landing.storyPreview.nextStory')}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            
            {/* Pagination indicators - Fixed the closing tag position */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {EXAMPLE_STORIES.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setAutoplay(false);
                    setDirection(index > currentStoryIndex ? 1 : -1);
                    setCurrentStoryIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full ${
                    index === currentStoryIndex ? "bg-white" : "bg-white/50"
                  }`}
                  aria-label={`${t('landing.storyPreview.goToStory')} ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

